import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCart, updateCartItem, removeCartItem, EshopCart } from '../services/eshopApi';
import { Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, IconButton, HStack, NumberInput, NumberInputField, Button, useToast } from '@chakra-ui/react';
import { FiTrash2 } from 'react-icons/fi';

const TrashIcon: React.ComponentType<any> = FiTrash2 as any;

const formatPrice = (cents: number, currency: string) => {
  const value = cents / 100;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(value);
};

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery<EshopCart>(['eshop-cart'], getCart);

  const handleQuantityChange = async (id: number, value: number) => {
    try {
      await updateCartItem(id, value);
      queryClient.invalidateQueries(['eshop-cart']);
    } catch {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se upravit košík.' });
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await removeCartItem(id);
      queryClient.invalidateQueries(['eshop-cart']);
    } catch {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se odebrat položku.' });
    }
  };

  if (isLoading) return <Text>Načítání košíku…</Text>;
  if (isError || !data) return <Text>Košík nelze načíst.</Text>;

  const total = data.items?.reduce((sum, it) => sum + it.unit_price_cents * it.quantity, 0) || 0;

  if (!data.items || data.items.length === 0) {
    return (
      <Box>
        <Heading size="lg" mb={2}>Košík je prázdný</Heading>
        <Text color="gray.600">Přidejte produkty z katalogu a vraťte se sem.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={4}>Košík</Heading>
      <Table size="sm" variant="simple" mb={4}>
        <Thead>
          <Tr>
            <Th>Produkt</Th>
            <Th isNumeric>Množství</Th>
            <Th isNumeric>Cena</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.items.map((it) => (
            <Tr key={it.id}>
              <Td>
                <Text fontWeight="medium">{it.product?.name || `Produkt #${it.product_id}`}</Text>
              </Td>
              <Td isNumeric>
                <HStack justify="flex-end">
                  <NumberInput
                    size="sm"
                    min={0}
                    value={it.quantity}
                    onChange={(_, val) => handleQuantityChange(it.id, val)}
                    maxW="90px"
                  >
                    <NumberInputField />
                  </NumberInput>
                </HStack>
              </Td>
              <Td isNumeric>{formatPrice(it.unit_price_cents * it.quantity, it.currency || data.currency)}</Td>
              <Td isNumeric>
                <IconButton
                  aria-label="Odebrat"
                  icon={<TrashIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemove(it.id)}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <HStack justify="space-between" mb={4}>
        <Text fontWeight="bold">Celkem:</Text>
        <Text fontWeight="bold">{formatPrice(total, data.currency || 'CZK')}</Text>
      </HStack>
      <Button colorScheme="blue" onClick={() => navigate('/checkout')}>
        Pokračovat k pokladně
      </Button>
    </Box>
  );
};

export default CartPage;
